class MaryssilPairedPurchase extends HTMLElement {
  #submitting = false;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.querySelector('[data-paired-submit]')?.addEventListener('click', this.#handleSubmit);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.querySelector('[data-paired-submit]')?.removeEventListener('click', this.#handleSubmit);
  }

  #handleChange = () => this.#sync();

  #selectedParts() {
    return Array.from(this.querySelectorAll('[data-pair-part]')).filter((part) => {
      const include = part.querySelector('[data-pair-include]');
      return include instanceof HTMLInputElement && include.checked;
    });
  }

  #sync() {
    const parts = Array.from(this.querySelectorAll('[data-pair-part]'));
    const mode = this.querySelector('[data-pair-mode]:checked')?.value || 'both';

    for (const part of parts) {
      const include = part.querySelector('[data-pair-include]');
      const role = part.getAttribute('data-pair-role');
      if (include instanceof HTMLInputElement) {
        include.checked = mode === 'both' || mode === role;
      }
    }

    const selectedParts = this.#selectedParts();

    for (const part of parts) {
      const include = part.querySelector('[data-pair-include]');
      const fieldset = part.querySelector('fieldset');
      const selected = include instanceof HTMLInputElement && include.checked;
      if (fieldset instanceof HTMLFieldSetElement) fieldset.disabled = !selected;
      part.toggleAttribute('data-included', selected);
      const state = part.querySelector('[data-include-label]');
      if (state) state.textContent = selected ? 'Selecionada' : 'Não incluída';
    }

    const complete =
      selectedParts.length > 0 &&
      selectedParts.every((part) => Boolean(part.querySelector('input[type="radio"]:checked')));
    const total = selectedParts.reduce((sum, part) => sum + Number(part.getAttribute('data-price') || 0), 0);
    const button = this.querySelector('[data-paired-submit]');
    const totalNode = this.querySelector('[data-paired-total]');
    const status = this.querySelector('[data-paired-status]');
    if (button instanceof HTMLButtonElement) {
      if (this.#submitting) {
        button.disabled = true;
        button.textContent = 'Adicionando à sacola…';
      } else {
        button.disabled = !complete;
        button.textContent = complete
          ? `Adicionar à sacola · ${this.#formatMoney(total)}`
          : 'Selecione o tamanho das peças incluídas';
      }
    }
    if (totalNode) totalNode.textContent = this.#formatMoney(total);
    if (status) {
      status.textContent = complete
        ? `${selectedParts.length === 2 ? 'Duas peças selecionadas' : 'Uma peça selecionada'}. Cada parte pode ser comprada separadamente.`
        : `Falta escolher ${selectedParts.length === 2 ? 'o tamanho de cada peça' : 'o tamanho da peça selecionada'}.`;
    }
  }

  #handleSubmit = async () => {
    if (this.#submitting) return;

    const items = this.#selectedParts()
      .map((part) => part.querySelector('input[type="radio"]:checked'))
      .filter((input) => input instanceof HTMLInputElement)
      .map((input) => {
        const part = input.closest('[data-pair-part]');
        const propertyName = part?.getAttribute('data-product-swatch-property')?.trim();
        const propertyValue = part?.getAttribute('data-product-swatch-value')?.trim();
        const properties = propertyName && propertyValue ? { [propertyName]: propertyValue } : undefined;
        return { variantId: input.value, quantity: 1, ...(properties ? { properties } : {}) };
      });

    if (items.length === 0) return;
    const productForm = this.closest('product-form-component');
    if (!productForm || typeof productForm.addItems !== 'function') return;

    const button = this.querySelector('[data-paired-submit]');
    this.#submitting = true;
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.textContent = 'Adicionando à sacola…';
    }

    try {
      const initialCart = await this.#fetchCart().catch(() => ({ item_count: 0 }));
      await productForm.addItems(items);
      const cart = await this.#waitForCartCount(Number(initialCart.item_count || 0) + items.length);
      if (button instanceof HTMLButtonElement) button.textContent = 'Adicionado à sacola';
      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        cartIcon.renderCartBubble(cart.item_count);
      }
      this.#openCartIfAutoOpenIsDisabled();
    } catch (_) {
      const status = this.querySelector('[data-paired-status]');
      if (status) status.textContent = 'Não foi possível atualizar a sacola. Tente novamente.';
    } finally {
      window.setTimeout(() => {
        this.#submitting = false;
        this.#sync();
      }, 900);
    }
  };

  async #fetchCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Não foi possível atualizar a sacola (${response.status}).`);
    return response.json();
  }

  async #waitForCartCount(expectedCount) {
    const timeoutAt = Date.now() + 6000;
    let cart = await this.#fetchCart();
    while (cart.item_count < expectedCount && Date.now() < timeoutAt) {
      await new Promise((resolve) => window.setTimeout(resolve, 150));
      cart = await this.#fetchCart();
    }
    if (cart.item_count < expectedCount) throw new Error('A sacola não confirmou todas as peças selecionadas.');
    return cart;
  }

  #openCartIfAutoOpenIsDisabled() {
    const cartDrawer = document.querySelector('#cart-drawer');
    const autoOpen = cartDrawer?.querySelector('cart-drawer-component[auto-open]');
    if (autoOpen || !(cartDrawer instanceof HTMLElement) || typeof cartDrawer.open !== 'function') return;
    cartDrawer.open();
  }

  #formatMoney(cents) {
    const currency = this.getAttribute('data-currency') || 'BRL';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  }
}

if (!customElements.get('maryssil-paired-purchase')) {
  customElements.define('maryssil-paired-purchase', MaryssilPairedPurchase);
}
